import React, { useRef } from 'react'
import {
  Link as ReactRouterLink,
  LinkProps as RouterLinkProps
} from 'react-router-dom'
import {
  Link as ChakraLink,
  LinkBox as ChakraLinkBox,
  LinkOverlay as ChakraLinkOverlay,
  LinkBoxProps,
  LinkOverlayProps as ChakraLinkOverlayProps,
  LinkProps
} from '@chakra-ui/react'
import { useLooopTheme } from '../../../public-api/hooks/useLooopTheme'
import { getFocusStyle } from '../../chakraThemeExtension'
import classnames from 'classnames'

export const Link: React.FC<LinkProps & { to?: string; dataTest?: string }> = ({
  dataTest = 'Link',
  ...props
}) => {
  const { theme } = useLooopTheme()
  return (
    <ChakraLink
      as={props.to ? ReactRouterLink : undefined}
      data-test={dataTest}
      color={theme.colors.linkText}
      tabIndex={props.tabIndex ?? 0}
      {...props}
    />
  )
}

/**
 * The NavLinkWithQueryParams - similar as
 * @param props
 * @returns
 */
export const LinkOverlayWithQuery: React.FC<
  ChakraLinkOverlayProps &
    RouterLinkProps & {
      activeClassName?: string
      to?: { pathname?: string; search?: string }
    }
> = (props) => {
  const { theme } = useLooopTheme()
  const { to, className, activeClassName, ...rest } = props
  return (
    <ChakraLinkOverlay
      as={ReactRouterLink}
      data-test="Link"
      tabIndex={props.tabIndex ?? 0}
      to={to}
      className={classnames(
        className,
        to?.pathname &&
          location.pathname.includes(to.pathname) &&
          `${activeClassName}`
      )}
      _focusVisible={getFocusStyle(theme.colors.buttonPrimary.mid)}
      {...rest}
    />
  )
}

/**
 * The LinkBox - as a link with onClick action
 * @param props
 * @returns
 */
export const LinkBox: React.FC<LinkBoxProps> = (props) => {
  const ref = useRef(null)
  const { theme } = useLooopTheme()

  return (
    <ChakraLinkBox
      ref={ref}
      role="button"
      cursor="pointer"
      tabIndex={!props.onClick ? -1 : props.tabIndex ?? 0}
      _focusVisible={
        !props.onClick && props.tabIndex === -1
          ? { outline: 'none' }
          : getFocusStyle(theme.colors.buttonPrimary.mid)
      }
      onKeyPress={(ev) => {
        // Accessibility - enter triggers click
        if (ev.key === 'Enter') {
          // @ts-expect-error
          props.onClick && ref?.current?.click()
        }
      }}
      position={'relative'} // Override Chakras default position static
      data-test="Link"
      {...props}
    />
  )
}

export type LinkOverlayProps = RouterLinkProps & ChakraLinkOverlayProps

/**
 * The LinkOverlay - as a classic react-router-dom link with 'to' prop
 * composes the chakra.a in addition to isExternal prop
 * @param props
 * @returns
 */
export const LinkOverlay: React.FC<LinkOverlayProps> = React.forwardRef(
  (props: LinkOverlayProps, ref: React.Ref<any>) => {
    const { to, ...rest } = props
    const { theme } = useLooopTheme()

    return (
      <ChakraLinkOverlay
        as={ReactRouterLink}
        ref={ref}
        role="button"
        cursor="pointer"
        to={(to as string).match(/^https?:\/\//) ? { pathname: to } : to}
        data-test="Link"
        tabIndex={props.tabIndex ?? 0}
        _focusVisible={getFocusStyle(theme.colors.buttonPrimary.mid)}
        position={'relative'} // Override Chakras default position static
        {...rest}
      />
    )
  }
)

export default Link
